import React from "react";

/*
 * Byggstenar för figurerna. Tecknad stil utan konturlinjer: formerna får
 * volym av mjuk cel-skuggning (en skuggmåne på sidan bort från ljuset),
 * högdager, reflekterat ljus i kanten och kontaktskuggor.
 * Ljuset kommer alltid snett uppifrån vänster.
 */

/** Unikt id-prefix per komponentinstans (för clipPath/gradienter). */
let uid = 0;
export const useToonId = (prefix: string) => {
  const ref = React.useRef<string>();
  if (!ref.current) {
    uid += 1;
    ref.current = `${prefix}${uid}`;
  }
  return ref.current;
};

/** Delade filter. Renderas en gång per scen. */
export const ToonDefs: React.FC = () => (
  <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
    <defs>
      <filter id="toon-soft" x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur stdDeviation="2.2" />
      </filter>
      <filter id="toon-softer" x="-40%" y="-40%" width="180%" height="180%">
        <feGaussianBlur stdDeviation="5" />
      </filter>
      <filter id="toon-glow" x="-60%" y="-60%" width="220%" height="220%">
        <feGaussianBlur stdDeviation="6" result="b" />
        <feMerge>
          <feMergeNode in="b" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
  </svg>
);

/** Kontaktskugga under en figur. */
export const Contact: React.FC<{ cx: number; cy: number; rx: number; ry?: number; color?: string; opacity?: number }> = ({
  cx,
  cy,
  rx,
  ry = rx * 0.16,
  color = "#000000",
  opacity = 0.35,
}) => <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill={color} opacity={opacity} filter="url(#toon-soft)" />;

export interface ShadeColors {
  base: string;
  shade: string;
  light: string;
  /** Reflekterat ljus i skuggkanten. */
  rim?: string;
}

/**
 * Valfri form med cel-skuggning. `d` är formen; skuggan är formen minus
 * en förskjuten kopia, så den följer konturen exakt.
 */
export const Shaded: React.FC<{
  d: string;
  c: ShadeColors;
  /** Skuggans förskjutning (px) – större = bredare skuggmåne. */
  depth?: number;
  highlight?: { cx: number; cy: number; rx: number; ry: number; rot?: number; opacity?: number };
  children?: React.ReactNode;
}> = ({ d, c, depth = 8, highlight, children }) => {
  const id = useToonId("sh");
  return (
    <g>
      <defs>
        <clipPath id={id}>
          <path d={d} />
        </clipPath>
      </defs>
      <path d={d} fill={c.shade} />
      <g clipPath={`url(#${id})`}>
        {c.rim && (
          <>
            {/* Smal ljuskant längst ut i skuggan */}
            <path d={d} fill={c.rim} />
            <path d={d} fill={c.shade} transform={`translate(${-depth * 0.14} ${-depth * 0.14})`} filter="url(#toon-soft)" />
          </>
        )}
        <path d={d} fill={c.base} transform={`translate(${-depth} ${-depth * 0.9})`} filter="url(#toon-soft)" />
        {highlight && (
          <ellipse
            cx={highlight.cx}
            cy={highlight.cy}
            rx={highlight.rx}
            ry={highlight.ry}
            transform={highlight.rot ? `rotate(${highlight.rot} ${highlight.cx} ${highlight.cy})` : undefined}
            fill={c.light}
            opacity={highlight.opacity ?? 0.8}
            filter="url(#toon-soft)"
          />
        )}
        {children}
      </g>
    </g>
  );
};

/** Klot med cel-skuggning. */
export const Sphere: React.FC<{ cx: number; cy: number; r: number; c: ShadeColors; children?: React.ReactNode }> = ({
  cx,
  cy,
  r,
  c,
  children,
}) => {
  const d = `M${cx - r} ${cy} a${r} ${r} 0 1 0 ${2 * r} 0 a${r} ${r} 0 1 0 ${-2 * r} 0 Z`;
  return (
    <Shaded d={d} c={c} depth={r * 0.28} highlight={{ cx: cx - r * 0.38, cy: cy - r * 0.42, rx: r * 0.3, ry: r * 0.18, rot: -35 }}>
      {children}
    </Shaded>
  );
};

/** Stort glansigt tecknat öga. (x, y) = mitten, look = pupillens förskjutning. */
export const Eye: React.FC<{
  x: number;
  y: number;
  rx: number;
  ry?: number;
  iris?: string;
  look?: [number, number];
  lid?: string;
}> = ({ x, y, rx, ry = rx * 1.2, iris = "#2b1a14", look = [0.15, 0.1], lid }) => {
  const ir = rx * 0.72;
  const ix = x + look[0] * rx;
  const iy = y + look[1] * ry;
  const id = useToonId("eye");
  return (
    <g>
      <defs>
        <clipPath id={id}>
          <ellipse cx={x} cy={y} rx={rx} ry={ry} />
        </clipPath>
        <radialGradient id={`${id}-i`} cx="50%" cy="65%" r="60%">
          <stop offset="0%" stopColor={iris} stopOpacity="0.55" />
          <stop offset="100%" stopColor={iris} />
        </radialGradient>
      </defs>
      <ellipse cx={x} cy={y} rx={rx} ry={ry} fill="#ffffff" />
      <g clipPath={`url(#${id})`}>
        <ellipse cx={x} cy={y - ry * 0.9} rx={rx * 1.2} ry={ry * 0.5} fill={lid ?? "#000000"} opacity={lid ? 1 : 0.12} />
        <ellipse cx={ix} cy={iy} rx={ir} ry={ir * 1.12} fill={`url(#${id}-i)`} />
        <ellipse cx={ix} cy={iy} rx={ir * 0.55} ry={ir * 0.62} fill="#0a0505" />
      </g>
      <ellipse cx={ix + ir * 0.3} cy={iy - ir * 0.42} rx={ir * 0.34} ry={ir * 0.3} fill="#ffffff" />
      <circle cx={ix - ir * 0.34} cy={iy + ir * 0.38} r={ir * 0.14} fill="#ffffff" opacity="0.85" />
    </g>
  );
};

/** Rosiga kinder. */
export const Blush: React.FC<{ x: number; y: number; r: number; color: string; opacity?: number }> = ({ x, y, r, color, opacity = 0.5 }) => (
  <ellipse cx={x} cy={y} rx={r} ry={r * 0.62} fill={color} opacity={opacity} filter="url(#toon-soft)" />
);

/** Liten gnistra (glitter i snö, stjärnstoft). */
export const Sparkle: React.FC<{ x: number; y: number; r: number; color?: string; className?: string; delay?: number }> = ({
  x,
  y,
  r,
  color = "#ffffff",
  className,
  delay = 0,
}) => (
  <path
    className={className}
    style={{ animationDelay: `${delay}s` }}
    d={`M${x} ${y - r} Q${x + r * 0.18} ${y - r * 0.18} ${x + r} ${y} Q${x + r * 0.18} ${y + r * 0.18} ${x} ${y + r} Q${x - r * 0.18} ${y + r * 0.18} ${x - r} ${y} Q${x - r * 0.18} ${y - r * 0.18} ${x} ${y - r} Z`}
    fill={color}
  />
);
